import { CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { DatePipe } from '@angular/common';
import { Component, DestroyRef, computed, effect, inject, input, viewChild } from '@angular/core';
import { MatFabButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { ButtonTaskActionsComponent } from '@app/button-task-actions/button-task-actions';
import { DurationComponent } from '@app/directives/duration';
import { ToolbarWidthSyncDirective } from '@app/directives/toolbar-width-sync';
import {
  KEYS_DELETE_TASK,
  KEYS_MARK_ACTIVE,
  KEYS_MARK_FINISHED,
  KEYS_RENAME,
  KEYS_START_STOP,
  hotkey,
} from '@app/domain/hotkeys';
import {
  Task,
  TaskState,
  filterTaskSessions,
  isTaskRunning,
  sessionDuration,
  sortSessions,
  taskDuration,
} from '@app/domain/task';
import { MapPipe } from '@app/pipes/map';
import { TaskStateIconPipe } from '@app/pipes/task-state-icon';
import { RoutedDialogs } from '@app/providers/routed-dialogs';
import { AppStore } from '@app/providers/state';
import { optionalDate } from '@app/utils/date';
import { HotkeysService } from 'angular2-hotkeys';
import { ButtonSessionActionsComponent } from './button-session-actions/button-session-actions';

@Component({
  selector: 'screen-task',
  templateUrl: './screen-task.html',
  styleUrls: ['./screen-task.scss'],
  imports: [
    TaskStateIconPipe,
    MatToolbarModule,
    MatIcon,
    MatTooltip,
    ButtonTaskActionsComponent,
    ButtonSessionActionsComponent,
    MapPipe,
    CdkDrag,
    CdkDropList,
    DatePipe,
    RouterLink,
    MatIconButton,
    MatFabButton,
    ScrollingModule,
    MatTableModule,
    DurationComponent,
    ToolbarWidthSyncDirective,
  ],
})
export default class ScreenTaskComponent {
  public store = inject(AppStore);
  private keys = inject<HotkeysService>(HotkeysService);
  private destroyRef = inject(DestroyRef);
  private routedDialogs = inject(RoutedDialogs);

  // Route inputs
  public readonly taskId = input<string>();
  public readonly from = input(undefined, { transform: optionalDate });
  public readonly to = input(undefined, { transform: optionalDate });

  currentTask = computed(() => {
    const taskId = this.taskId();
    if (!taskId) return undefined;
    const task = this.store.tasks()[taskId];
    if (!task) return undefined;
    return filterTaskSessions(task, { from: this.from(), to: this.to() });
  });

  taskIsInProgress = computed(() => isTaskRunning(this.currentTask()));
  viewport = viewChild(CdkVirtualScrollViewport);
  taskDuration = taskDuration;
  sessionDuration = sessionDuration;
  sortSessions = sortSessions;
  trackSession = (_: number, session: Task['sessions'][number]) => `${session.start}-${session.end ?? 'running'}`;

  hotkeys = [
    hotkey(KEYS_START_STOP, 'Start/stop task', () => {
      const task = this.currentTask();
      const inProgress = this.taskIsInProgress();
      if (!task) return;
      if (inProgress) {
        this.stop(task.id);
      } else {
        this.start(task.id);
      }
    }),
    hotkey(KEYS_MARK_FINISHED, `Mark as finished`, () => {
      const task = this.currentTask();
      if (task) this.store.updateTaskState(task.id, TaskState.finished);
    }),
    hotkey(KEYS_MARK_ACTIVE, `Mark as active`, () => {
      const task = this.currentTask();
      if (task) this.store.updateTaskState(task.id, TaskState.active);
    }),
    hotkey(KEYS_RENAME, 'Rename task', () => {
      const task = this.currentTask();
      if (task) this.routedDialogs.navigate(['tasks', task.id, 'rename']);
    }),
    hotkey(KEYS_DELETE_TASK, 'Delete task', () => {
      const task = this.currentTask();
      if (task) this.store.deleteTask(task.id);
    }),
  ];
  displayedColumns = ['start', 'end', 'duration', 'action'];

  constructor() {
    this.keys.add(this.hotkeys);
    this.destroyRef.onDestroy(() => {
      this.keys.remove(this.hotkeys);
    });
    effect(() => {
      this.taskId();
      this.viewport()?.scrollToIndex(0);
    });
  }
  start(taskId: string) {
    this.store.startTask(taskId, Date.now());
  }
  stop(taskId: string) {
    this.store.stopTask(taskId, Date.now());
  }
}
